import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Lightbulb, 
  Box, 
  Type, 
  Layers, 
  Sun, 
  Square, 
  PanelTop,
  Building2,
  Phone,
  Mail,
  Menu,
  X,
  ChevronRight,
  CheckCircle2,
  Zap
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// User provided images
const IMAGES = {
  mupis: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/r0cmyj7n_Mupis%20.jpg",
  palas: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/xkcii9is_Palas.jpg",
  mupisVerticais: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/3zfvgbla_Mupis%20-%20Verticais.jpg",
  aluminioInox: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/66x2xbzz_Alumi%CC%81nio%20-%20Alucobond%20-%20Inox.jpg",
  monopostesCaixas: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/hsijo7kw_Monopostes%20-%20Caixas%20de%20luz.jpg",
  letrasLataoPolido: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/sjxrckvm_Letras%20Lata%CC%83o%20Polido.jpg",
  letrasInoxPolido: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/9emvppyu_Letras%20Inox%20Polido.jpg",
  letrasInoxEscovado: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/kebjmonb_Letras%20Inox%20Escovado.jpg",
  letrasCorporeas: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/t1y4p0ha_Letras%20Corpo%CC%81reas.jpg",
  letrasMonobloco: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/asguyfp3_Letras%20MonoBloco.jpg",
  corpoAluminio: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/vkjjdbza_Corpo%20Alumi%CC%81nio.jpg",
  letras3D: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/2ycmpmu0_Letras%203D.jpg",
  frisosNeon: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/q3b4y8at_Frisos%20Ne%CC%81on%20-%20Logotipos%20Ne%CC%81on.jpg",
  iluminacaoLED: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/ognkiiko_Iluminac%CC%A7a%CC%83o%20Leds%20-%20RGB.jpg",
  paineisPlacards: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/xafe8gsc_Paine%CC%81is%20-%20Placards%20-%20Placas.jpg",
  heroBackground: "https://images.unsplash.com/photo-1745448797905-36e057c09088?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxtZXRhbCUyMGZhYnJpY2F0aW9uJTIwc3BhcmtzJTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3NzU1NTUxNTZ8MA&ixlib=rb-4.1.0&q=85",
  aboutImage: "https://customer-assets.emergentagent.com/job_luz-exterior/artifacts/tqo4fl0x_sobre%20nos.jpg"
};

// Services data - all with real images
const SERVICES = [
  { id: 1, name: "Iluminação LED (RGB)", image: IMAGES.iluminacaoLED, span: "span-2" },
  { id: 2, name: "Frisos Néon / Logotipos Néon", image: IMAGES.frisosNeon },
  { id: 3, name: "Letras 3D", image: IMAGES.letras3D },
  { id: 4, name: "Letras Monobloco", image: IMAGES.letrasMonobloco },
  { id: 5, name: "Letras Corpóreas", image: IMAGES.letrasCorporeas, span: "span-2" },
  { id: 6, name: "Corpo Alumínio", image: IMAGES.corpoAluminio },
  { id: 7, name: "Monopostes / Caixas de luz", image: IMAGES.monopostesCaixas, span: "span-2" },
  { id: 8, name: "Letras em Inox Escovado", image: IMAGES.letrasInoxEscovado },
  { id: 9, name: "Alumínio / Alucobond / Inox", image: IMAGES.aluminioInox },
  { id: 10, name: "Letras em Inox Polido", image: IMAGES.letrasInoxPolido, span: "span-2" },
  { id: 11, name: "Letras em Latão Polido", image: IMAGES.letrasLataoPolido },
  { id: 12, name: "Mupis", image: IMAGES.mupis },
  { id: 13, name: "Mupis / Estruturas Verticais", image: IMAGES.mupisVerticais, span: "span-2" },
  { id: 14, name: "Painéis / Placards / Placas", image: IMAGES.paineisPlacards },
  { id: 15, name: "Palas", image: IMAGES.palas },
];

// Process steps
const PROCESS_STEPS = [
  { number: "1", title: "Orçamento", description: "Contacte-nos com os detalhes do seu projeto. Analisamos as suas necessidades e apresentamos uma proposta personalizada." },
  { number: "2", title: "Produção", description: "Após aprovação, iniciamos o fabrico na nossa unidade industrial com materiais de alta qualidade." },
  { number: "3", title: "Instalação", description: "Equipa especializada realiza a instalação no local, garantindo um acabamento perfeito." },
];

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white border-b-2 border-[#111111] z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <span className="font-black text-xl tracking-tight uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Reclamos <span className="text-[#FFC107]">Luminosos</span>
            </span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button onClick={() => scrollToSection('servicos')} className="nav-link" data-testid="nav-servicos">Serviços</button>
            <button onClick={() => scrollToSection('processo')} className="nav-link" data-testid="nav-processo">Processo</button>
            <button onClick={() => scrollToSection('sobre')} className="nav-link" data-testid="nav-sobre">Sobre</button>
            <button onClick={() => scrollToSection('contacto')} className="btn-primary text-sm py-2 px-4" data-testid="nav-contacto">
              Contacto
            </button>
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2" 
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden border-t-2 border-[#111111] bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button onClick={() => scrollToSection('servicos')} className="block w-full text-left px-3 py-2 nav-link">Serviços</button>
              <button onClick={() => scrollToSection('processo')} className="block w-full text-left px-3 py-2 nav-link">Processo</button>
              <button onClick={() => scrollToSection('sobre')} className="block w-full text-left px-3 py-2 nav-link">Sobre</button>
              <button onClick={() => scrollToSection('contacto')} className="block w-full text-left px-3 py-2 nav-link text-[#FFC107]">Contacto</button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section - Single Column with Background Image
const HeroSection = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contacto');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-[90vh] pt-16 flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src={IMAGES.letrasLataoPolido} 
          alt="Reclamos Luminosos" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111111]/95 via-[#111111]/80 to-[#111111]/40"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <p className="text-[#FFC107] font-bold uppercase tracking-[0.2em] text-sm mb-4">
            Publicidade Exterior
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tighter uppercase text-white mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Reclamos Luminosos<br />
            <span className="text-[#FFC107]">de Qualidade</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-lg leading-relaxed">
            Fabrico próprio de reclamos luminosos, letras corpóreas e soluções visuais personalizadas para dar destaque à sua empresa.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <button 
              onClick={scrollToContact} 
              className="btn-primary flex items-center gap-2"
              data-testid="hero-cta"
            >
              Pedir Orçamento
              <ChevronRight size={20} />
            </button>
            
            <div className="flex items-center gap-3 bg-[#111111]/60 border-2 border-[#FFC107] px-6 py-3">
              <Phone className="text-[#FFC107]" size={20} />
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Ligue agora</p>
                <a href="tel:968520909" className="text-white font-bold text-lg hover:text-[#FFC107] transition-colors">
                  96 852 09 09
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#FFC107]"></div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="servicos" className="py-24 sm:py-32 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <p className="section-overline">O Que Fazemos</p>
          <h2 className="section-title">Nossos Serviços</h2>
        </div>
        
        <div className="services-grid">
          {SERVICES.map((service) => (
            <div 
              key={service.id}
              className={`service-card ${service.image ? 'with-image' : ''} ${service.dark ? 'dark' : ''} ${service.span || ''}`}
              style={service.image ? { backgroundImage: `url(${service.image})` } : {}}
              data-testid={`service-card-${service.id}`}
            >
              {service.icon && !service.image && (
                <service.icon className={`service-icon mb-4 ${service.dark ? 'text-[#FFC107]' : 'text-[#111111]'}`} size={32} />
              )}
              <h3 className={`service-title font-bold text-lg uppercase tracking-tight ${service.dark && !service.image ? 'text-white' : 'text-[#111111]'}`} style={{ fontFamily: 'Chivo, sans-serif' }}>
                {service.name}
              </h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  return (
    <section id="processo" className="py-24 sm:py-32 bg-white" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16">
          <p className="section-overline">Como Funciona</p>
          <h2 className="section-title">Processo de Trabalho</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PROCESS_STEPS.map((step, index) => (
            <div 
              key={index} 
              className="process-step"
              data-testid={`process-step-${index + 1}`}
            >
              <div className="process-number">{step.number}</div>
              <h3 className="font-bold text-xl uppercase mb-4 text-[#111111]" style={{ fontFamily: 'Chivo, sans-serif' }}>{step.title}</h3>
              <p className="text-[#2B2B2B] leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="sobre" className="py-24 sm:py-32 bg-[#111111]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="section-overline">Sobre Nós</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase text-white mb-8" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Experiência e <span className="text-[#FFC107]">Qualidade</span>
            </h2>
            <div className="space-y-6 text-gray-300 leading-relaxed">
              <p>
                Com anos de experiência no sector da publicidade exterior, especializamo-nos no fabrico de reclamos luminosos, letras corpóreas e soluções visuais que destacam a identidade da sua empresa.
              </p>
              <p>
                A nossa unidade de produção própria permite-nos garantir o controlo total da qualidade, desde a concepção até à instalação final. Trabalhamos com materiais de primeira qualidade e as mais recentes tecnologias de iluminação LED.
              </p>
              <p>
                Cada projeto é único. Por isso, oferecemos soluções totalmente personalizadas, adaptadas às necessidades específicas de cada cliente e espaço.
              </p>
            </div>
            
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { icon: CheckCircle2, text: "Fabrico Próprio" },
                { icon: CheckCircle2, text: "Soluções Personalizadas" },
                { icon: CheckCircle2, text: "Instalação Especializada" },
                { icon: CheckCircle2, text: "Qualidade Garantida" },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-white">
                  <item.icon className="text-[#FFC107]" size={20} />
                  <span className="text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="border-2 border-[#FFC107]">
              <img 
                src={IMAGES.aboutImage} 
                alt="Fabrico de reclamos" 
                className="w-full h-auto"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#FFC107] hidden lg:block"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quote`, formData);
      toast.success('Pedido enviado com sucesso! Entraremos em contacto brevemente.');
      setFormData({ nome: '', telefone: '', email: '', mensagem: '' });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Erro ao enviar pedido. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 sm:py-32 bg-[#F5F5F5] border-t-2 border-[#111111]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <p className="section-overline">Contacto</p>
            <h2 className="section-title mb-8">Peça o Seu Orçamento</h2>
            <p className="text-[#2B2B2B] text-lg mb-8 leading-relaxed">
              Interessado nos nossos serviços? Entre em contacto connosco para discutir o seu projeto e receber um orçamento personalizado.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#111111] flex items-center justify-center">
                  <Phone className="text-[#FFC107]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-[#2B2B2B] uppercase tracking-wide font-bold">Telefone</p>
                  <a href="tel:968520909" className="text-xl font-bold text-[#111111] hover:text-[#FFC107] transition-colors" data-testid="contact-phone">
                    96 852 09 09
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#111111] flex items-center justify-center">
                  <Mail className="text-[#FFC107]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-[#2B2B2B] uppercase tracking-wide font-bold">Email</p>
                  <a href="mailto:grupo@alislux.pt" className="text-xl font-bold text-[#111111] hover:text-[#FFC107] transition-colors" data-testid="contact-email">
                    grupo@alislux.pt
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="bg-white border-2 border-[#111111] p-6 sm:p-8" style={{ boxShadow: '6px 6px 0px #111111' }}>
            <h3 className="font-bold text-xl uppercase mb-6 text-[#111111]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Formulário de Contacto
            </h3>
            
            <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
              <div className="form-group">
                <label htmlFor="nome" className="form-label">Nome *</label>
                <input
                  type="text"
                  id="nome"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  className="input-industrial"
                  placeholder="O seu nome"
                  data-testid="input-nome"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="telefone" className="form-label">Telefone *</label>
                <input
                  type="tel"
                  id="telefone"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  required
                  className="input-industrial"
                  placeholder="O seu número de telefone"
                  data-testid="input-telefone"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-industrial"
                  placeholder="O seu email"
                  data-testid="input-email"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="mensagem" className="form-label">Mensagem *</label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  value={formData.mensagem}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="input-industrial resize-none"
                  placeholder="Descreva o seu projeto ou pedido..."
                  data-testid="input-mensagem"
                />
              </div>
              
              <button 
                type="submit" 
                className="btn-primary w-full flex items-center justify-center gap-2"
                disabled={isSubmitting}
                data-testid="submit-btn"
              >
                {isSubmitting ? 'A Enviar...' : 'Enviar Pedido'}
                {!isSubmitting && <ChevronRight size={20} />}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#111111] border-t-2 border-[#FFC107] py-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-white">
            <span className="font-black text-xl tracking-tight uppercase" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Reclamos <span className="text-[#FFC107]">Luminosos</span>
            </span>
          </div>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <a href="tel:968520909" className="hover:text-[#FFC107] transition-colors">96 852 09 09</a>
            <span>|</span>
            <a href="mailto:grupo@alislux.pt" className="hover:text-[#FFC107] transition-colors">grupo@alislux.pt</a>
          </div>
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} Todos os direitos reservados
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#111111',
            color: '#FFFFFF',
            border: '2px solid #FFC107',
            borderRadius: '0',
          },
        }}
      />
      <Navigation />
      <HeroSection />
      <ServicesSection />
      <ProcessSection />
      <AboutSection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
